/**
 * #514 — REFERENCIAS LEGIBLES para los campos de texto libre de auditoría.
 *
 * ===========================================================================
 * LANE PARITY — este archivo tiene un GEMELO BYTE-EQUIVALENTE en
 * `mcp-server/src/utils/auditRef.js`.
 * ===========================================================================
 * No se importan entre sí a propósito: `src/**` es el bundle del browser y
 * `mcp-server/**` es un paquete Node aparte con su propio package.json (misma
 * razón que `materialSplit.js` / `rawTextFidelity.js`). La duplicación es
 * exactamente donde los carriles derivan (#353, #454), así que hay un test de
 * paridad que corre AMBAS copias contra la misma tabla de casos:
 * `mcp-server/tests/tools/auditRefLaneParity.test.js`. Si tocás una, tocá la otra.
 *
 * ---------------------------------------------------------------------------
 * EL PROBLEMA QUE RESUELVE
 * ---------------------------------------------------------------------------
 * #336 arregló el caso del feed en el origen y dejó un `stripUuids` en la
 * pasada de RENDER para el texto histórico. Pero varios sitios seguían
 * ESCRIBIENDO el uuid interpolado dentro de `notes`:
 *
 *     p_notes: `MR ${request_id}`                                  <- MCP
 *     p_notes: `Solicitud ${requestId}`                            <- web (¡ya divergían!)
 *     p_notes: `MR ${request_id} item ${plan.item.id}`
 *     notes:   `Draft PO from material request ${request_id}`
 *     notes:   `Auto-linked from PO receipt (purchase_item ${item_id})`
 *
 * `notes` es un campo de auditoría que también leen humanos por SQL y por
 * export, no sólo la UI. Y un uuid embebido en prosa NO es un vínculo: no se
 * puede joinear, no sobrevive un rename, y si la fila apuntada se borra queda
 * un identificador huérfano que parece dato.
 *
 * La regla, la misma que aplicó #336 con `fulfilled_assignment_id`: la
 * RELACIÓN vive en la columna FK, y en `notes` va la referencia LEGIBLE.
 *
 * ---------------------------------------------------------------------------
 * POR QUÉ NO HAY FALLBACK AL UUID
 * ---------------------------------------------------------------------------
 * Sería volver a introducir el bug por la puerta de atrás. Cuando no hay
 * referencia legible, la nota se queda sin número: la relación ya está en la
 * FK, así que no se pierde nada joineable — sólo un poco de comodidad al leer.
 *
 * Medido en producción (2026-07-29): `material_requests.request_number` está
 * poblado en 43 de 84 filas, y el trigger `set_material_request_number_trigger`
 * lo genera en cada INSERT — o sea que las 41 sin número son anteriores al
 * trigger y no se van a crear más. Toda solicitud NUEVA tiene número.
 *
 * @module utils/auditRef
 */

/** Normaliza a string no vacío, o null. */
const clean = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

/**
 * Referencia legible de una solicitud de material: su `request_number`
 * (`MR-2026-0039`), nunca su uuid.
 *
 * @param {object|null} request fila (o parcial) de `material_requests`
 * @returns {string|null}
 */
export const materialRequestRef = (request) =>
  request ? clean(request.request_number) : null;

/**
 * Descripción legible de un ítem de solicitud. Nunca el uuid del ítem.
 *
 * @param {object|null} item fila (o parcial) de `material_request_items`
 * @returns {string|null}
 */
export const materialRequestItemRef = (item) =>
  item ? (clean(item.item_description) || clean(item.ai_raw_text)) : null;

/**
 * Nota para `stock_movements.notes` al consumir por FIFO desde una solicitud.
 * La relación ya vive en `material_requests.stock_movement_id` /
 * `material_request_items.stock_movement_id`, que el caller escribe justo
 * después — acá sólo va texto para humanos.
 *
 * @param {object} args
 * @param {object|null} args.request  fila de `material_requests`
 * @param {object|null} [args.item]   fila de `material_request_items`
 * @param {string|null} [args.userNote] nota que escribió la persona
 * @returns {string}
 */
export const materialConsumptionNote = ({ request, item = null, userNote = null }) => {
  const ref = materialRequestRef(request);
  const itemRef = materialRequestItemRef(item);
  const head = ref ? `Solicitud de material ${ref}` : 'Solicitud de material';
  return [itemRef ? `${head} — ${itemRef}` : head, clean(userNote)]
    .filter(Boolean)
    .join(' — ');
};

/**
 * Nota para `purchases.notes` de la orden BORRADOR creada al aprobar una
 * solicitud. La relación vive en
 * `material_request_items.purchase_item_id -> purchase_items.purchase_id`.
 *
 * @param {object|null} request fila de `material_requests`
 * @returns {string}
 */
export const draftPurchaseNote = (request) => {
  const ref = materialRequestRef(request);
  return ref
    ? `Orden borrador desde la solicitud de material ${ref}`
    : 'Orden borrador desde una solicitud de material';
};

/**
 * Nota para `task_materials.notes` cuando se autovincula al recibir una
 * compra. La relación vive en `task_materials.purchase_item_id` (columna
 * agregada por la migración `..._task_materials_purchase_item_fk.sql`, #514).
 *
 * @param {object} args
 * @param {object|null} [args.purchase] fila de `purchases` (para `invoice_number`)
 * @param {object|null} [args.line]     fila de `purchase_items` (para `description`)
 * @returns {string}
 */
export const purchaseReceiptMaterialNote = ({ purchase = null, line = null }) => {
  const invoice = purchase ? clean(purchase.invoice_number) : null;
  const description = line ? clean(line.description) : null;
  const head = invoice
    ? `Vinculado automáticamente al recibir la compra ${invoice}`
    : 'Vinculado automáticamente al recibir una compra';
  return description ? `${head} — ${description}` : head;
};
